import { ZAGREB } from "@fiskhub/shared";

/**
 * Everything a Croatian tax inspector would look at is rendered in
 * Europe/Zagreb, matching what the fiscal payload carried. Never the browser's
 * local zone — an operator travelling would otherwise see different times than
 * the receipt shows.
 */

const dateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: ZAGREB,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const dateFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: ZAGREB,
  year: "numeric",
  month: "short",
  day: "2-digit",
});

function toDate(value: string | Date | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `01/09/2026, 14:32:07` in Europe/Zagreb. Em dash when absent. */
export function formatDateTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? dateTimeFormatter.format(date) : "—";
}

/** `01 Sep 2026` in Europe/Zagreb. */
export function formatDay(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? dateFormatter.format(date) : "—";
}

/**
 * Money stays a string end to end (§4.1) — this only adds thousands separators
 * for display and never parses to a float.
 */
export function formatMoney(amount: string, currency = "EUR"): string {
  const negative = amount.startsWith("-");
  const bare = negative ? amount.slice(1) : amount;
  const dot = bare.indexOf(".");
  const whole = dot === -1 ? bare : bare.slice(0, dot);
  const fraction = dot === -1 ? "00" : bare.slice(dot + 1);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${negative ? "-" : ""}${grouped}.${fraction} ${currency}`;
}

const MS_PER_DAY = 86_400_000;

/** Whole days from now until `value`. Negative once it is in the past. */
export function daysUntil(value: string | Date | null | undefined, now = new Date()): number | null {
  const date = toDate(value);
  if (!date) return null;
  return Math.floor((date.getTime() - now.getTime()) / MS_PER_DAY);
}

/** "in 12 days" / "3 days ago" / "today". */
export function relativeDays(days: number): string {
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}

export function relativeTime(value: string | Date | null | undefined, now = new Date()): string {
  const date = toDate(value);
  if (!date) return "never";
  const deltaMs = now.getTime() - date.getTime();
  const minutes = Math.round(deltaMs / 60_000);
  if (Math.abs(minutes) < 1) return "just now";
  if (Math.abs(minutes) < 60) return `${Math.abs(minutes)}m ${minutes >= 0 ? "ago" : "from now"}`;
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return `${Math.abs(hours)}h ${hours >= 0 ? "ago" : "from now"}`;
  const days = Math.round(hours / 24);
  return relativeDays(-days);
}

/** `1234` -> `1,234`. For counts, never money. */
export function formatCount(value: number): string {
  return new Intl.NumberFormat("en-GB").format(value);
}

export function formatPercent(ratio: number, digits = 1): string {
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** Never show a full OIB in a dense list without it being obviously an OIB. */
export function formatOib(oib: string): string {
  return oib.length === 11 ? `${oib.slice(0, 2)} ${oib.slice(2, 6)} ${oib.slice(6)}` : oib;
}

/**
 * `abcdefgh…stuvwxyz`. Returns "—" for a value that is not there.
 *
 * The nullish guard is not decoration. This is called on identifiers that the
 * API may legitimately not have yet — an invoice has no JIR until the Tax
 * Administration confirms it — and it renders inside table cells, where an
 * unguarded `.length` on `undefined` throws through the row map and takes the
 * whole screen down rather than blanking one cell.
 */
export function truncateMiddle(value: string | null | undefined, keep = 8): string {
  if (!value) return "—";
  if (value.length <= keep * 2 + 1) return value;
  return `${value.slice(0, keep)}…${value.slice(-keep)}`;
}
